import { type FC, useRef, useCallback } from 'react';
import { useFragment, graphql } from 'react-relay';
import classnames from 'classnames';
import { breakpoints } from 'dibs-sassy/exports/breakpoints';
import { spacing } from 'dibs-sassy/exports/spacing';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet';
import { SeoLink } from 'dibs-seo/exports/SeoLink';
import ArrowRight from 'dibs-icons/exports/legacy/ArrowRight';
import {
    trackEvent,
    eventNameConstants,
    interactionTypeConstants,
    triggerConstants,
} from 'dibs-tracking';

import { useHomePageContext } from '../HomePageContext';

import { type HpSharedFeaturedModuleItemVariant_onSaleVertical$key } from './__generated__/HpSharedFeaturedModuleItemVariant_onSaleVertical.graphql';

import dibsCss from 'dibs-css';
import styles from './HpSharedFeaturedModuleItemVariant.scss';

const onSaleVerticalFragment = graphql`
    fragment HpSharedFeaturedModuleItemVariant_onSaleVertical on OnSaleVertical {
        category
        displayName
        linkData {
            ...SeoLink_linkData
        }
        firstItemPhotoWebPath(size: master)
    }
`;

const RESP_CIRCLE = 126;
const RESP_IMG = 63;
const RESP_IMG_SCALED = Math.ceil(RESP_IMG * 1.1);
const RESP_IMG_MIN = 70;

const MOBILE_CIRCLE = 99;
const MOBILE_IMG = 54;

const categoryToStepInteractionName = ({ category }: { category?: string | null }): string => {
    if (!category) {
        return '';
    }

    const categoryLabel = {
        A: 'Art',
        V: 'Fashion',
        J: 'Jewelry',
        F: 'Furniture',
    };

    return `${categoryLabel[category as keyof typeof categoryLabel]} Clicked`;
};

export const HpSharedFeaturedModuleItemVariant: FC<{
    onSaleVertical?: HpSharedFeaturedModuleItemVariant_onSaleVertical$key | null;
}> = ({ onSaleVertical: onSaleVerticalRef }) => {
    const { isMobile } = useHomePageContext();
    const elementRef = useRef<HTMLDivElement>(null);
    const onSaleVertical = useFragment(onSaleVerticalFragment, onSaleVerticalRef);

    const { category, displayName, linkData, firstItemPhotoWebPath: imgSrc } = onSaleVertical || {};

    const onClick = useCallback(() => {
        trackEvent({
            eventName: eventNameConstants.EVENT_PRODUCT_INTERACTION,
            interaction_type: interactionTypeConstants.INTERACTION_TYPE_SALE_MODULE,
            step_interaction_name: categoryToStepInteractionName({ category }),
            trigger: triggerConstants.TRIGGER_SALE_MODULE,
        });
    }, [category]);

    if (!linkData || !imgSrc || !displayName || !category) {
        return null;
    }

    // imgDisplaySize is the size of the image that is displayed on the screen
    // imgScaledSize is the size of the image that is scaled on hover
    const imgDisplaySize = isMobile ? MOBILE_IMG : RESP_IMG;
    const imgScaledSize = isMobile ? MOBILE_IMG : RESP_IMG_SCALED;
    const circleSize = isMobile ? MOBILE_CIRCLE : RESP_CIRCLE;

    return (
        <SeoLink
            linkData={linkData}
            className={classnames(styles.link, dibsCss.flex, dibsCss.itemsCenter, dibsCss.wFull, {
                [dibsCss.flexCol]: isMobile,
                [dibsCss.gapSmaller]: isMobile,
                [dibsCss.gapSmall]: !isMobile,
            })}
            style={{
                // Need to deduct the gap between the items for mobile so it would be visible
                flexBasis: isMobile ? `calc(50% - ${spacing.sassySpacingXSmall})` : undefined,
            }}
            onClick={onClick}
        >
            <div
                ref={elementRef}
                className={classnames(
                    dibsCss.aspectSquare,
                    dibsCss.overflowHidden,
                    dibsCss.roundedFull,
                    dibsCss.bgWhite,
                    dibsCss.flex,
                    dibsCss.justifyCenter,
                    dibsCss.itemsCenter,
                    dibsCss.hFull,
                    dibsCss.wFull,
                    dibsCss.pSmall
                )}
                style={{
                    maxWidth: `${circleSize}px`,
                    minWidth: `${RESP_IMG_MIN}px`,
                }}
            >
                <img
                    src={`${imgSrc}?height=${imgScaledSize}`}
                    alt={displayName}
                    className={classnames(dibsCss.hFull, dibsCss.wFull, dibsCss.objectContain, {
                        [styles.img]: !isMobile,
                    })}
                    style={{ maxWidth: `${imgDisplaySize}px`, maxHeight: `${imgDisplaySize}px` }}
                    srcSet={getSrcsetString(imgSrc, [
                        RESP_IMG_SCALED,
                        RESP_IMG_SCALED * 2,
                        MOBILE_IMG,
                        MOBILE_IMG * 2,
                    ])}
                    sizes={`(max-width: ${breakpoints.mobile.max}px) ${MOBILE_IMG}px, ${RESP_IMG}px`}
                />
            </div>

            <div
                className={classnames(
                    styles.text,
                    dibsCss.flex,
                    dibsCss.itemsCenter,
                    dibsCss.gapXxsmall,
                    dibsCss.flexShrink,
                    dibsCss.flexGrow,
                    {
                        [dibsCss.textCenter]: isMobile,
                        [dibsCss.sassyFontBody]: isMobile,
                        [dibsCss.sassyFontBodyMedium]: !isMobile,
                    }
                )}
                style={{ minWidth: '110px' }}
            >
                {displayName}
                {!isMobile && (
                    <ArrowRight
                        className={classnames(
                            dibsCss.wSmaller,
                            dibsCss.hSmaller,
                            dibsCss.flexShrink0
                        )}
                    />
                )}
            </div>
        </SeoLink>
    );
};
